'use client';

declare global {
  interface Window {
    gtag: (...args: unknown[]) => void;
    dataLayer: unknown[];
  }
}

const GA_ID = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;

function isEnabled(): boolean {
  return typeof window !== 'undefined' && !!GA_ID && !!window.gtag;
}

/** Track a page view — called automatically by GoogleAnalytics component */
export function trackPageView(url: string): void {
  if (!isEnabled()) return;
  window.gtag('config', GA_ID as string, { page_path: url });
}

/** Generic event tracker */
export function trackEvent(
  eventName: string,
  params: Record<string, string | number | boolean> = {}
): void {
  if (!isEnabled()) return;
  window.gtag('event', eventName, params);
}

// ─── Booking Funnel ──────────────────────────────────────────────────────────

/** Fired when visitor starts the booking wizard (step 1 viewed) */
export function trackBookingStart(): void {
  trackEvent('begin_checkout', { event_category: 'booking_funnel', event_label: 'step_1_date' });
}

/** Fired on each wizard step advance */
export function trackBookingStep(step: number, stepLabel: string): void {
  trackEvent('checkout_progress', {
    event_category: 'booking_funnel',
    checkout_step: step,
    event_label: stepLabel,
  });
}

/** Fired when payment is initiated (PayFast redirect) */
export function trackPaymentInitiated(total: number, players: number, holes: number): void {
  trackEvent('add_payment_info', {
    event_category: 'booking_funnel',
    value: total,
    currency: 'ZAR',
    items: `${players}p_${holes}h`,
  });
}

/** Fired on successful booking confirmation */
export function trackBookingConversion(total: number, bookingId: string, players: number, holes: number): void {
  trackEvent('purchase', {
    transaction_id: bookingId,
    value: total,
    currency: 'ZAR',
    event_category: 'booking_conversion',
    items: `${players}p_${holes}h`,
  });
}

// ─── Traffic Sources & Engagement ────────────────────────────────────────────

/** Fired when a CTA button is clicked */
export function trackCTAClick(ctaLabel: string, location: string): void {
  trackEvent('cta_click', { event_category: 'engagement', event_label: ctaLabel, location });
}

/** Fired when user views a key section (hero, pricing, membership) */
export function trackSectionView(sectionName: string): void {
  trackEvent('section_view', { event_category: 'page_performance', event_label: sectionName });
}

/** Fired on login / registration */
export function trackAuth(action: 'login' | 'register', method: string = 'email'): void {
  trackEvent(action, { event_category: 'auth', method });
}
